//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Grid, Checkbox } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowForward';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Input, { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input/input';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext, AppContext } from '../../../common/contexts';
import useCountryStateCity from '../../../common/hooks/useCountryStateCity';
import { setClassNameInDefns } from '../../../common/definitionHelper';
// import AfitdTypography from '../../../common/elemental/AfitdTypography';
// import AfitdTextField from '../../../common/elemental/AfitdTextField';
import AfitdTextField from '../../../common/elemental/AfitdTextField';
import AfitdCountryAutocomplete from '../../../common/elemental/AfitdCountryAutocomplete';
import AfitdButton from '../../../common/elemental/AfitdButton';
import AfitdLink from '../../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { validateEmail } from '../../../common/emailHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import BorderLinearProgress from '../BorderLinearProgress';
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _includes from 'lodash/includes';

import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    // root: { padding: 20 },
    exampleWithNoPropsOnlyStyles: { color: 'red', marginTop: 30, marginBottom: 40 },
    nonReactElementExample: { color: 'blue', marginTop: 50, cursor: 'pointer' },
    container: {
        width: '100%',
        // height: '640px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '48px 112px 48px 112px',
        [theme.breakpoints.down('sm')]: { padding: '44px 0px' },
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '18px',
        },
    },
    fieldLabel: {
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '16px',
        marginTop: '20px',
    },
    progressGrid: {
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: { justifyContent: 'center', margin: '0 auto', width: '100%' },
    },
    gridBody: {
        marginTop: '38px',
    },
    gridBodyFooter: { marginTop: '38px', [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' } },
    nextButton: {
        textAlign: 'right',
        marginTop: '16px',
        marginRight: 5,
        [theme.breakpoints.down('sm')]: { marginRight: 0 },
    },
    footerGridItem: {
        [theme.breakpoints.down('sm')]: { paddingRight: 8 },
    },
    termsAndConditionText: { fontWeight: 'bold' },
    termsOfConditionLink: {
        color: '#374EF5',
        textDecoration: 'underline',
        fontWeight: 'bold',
    },
    termsAndConditionsGrid: { display: 'flex' },
    phoneInput: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #c4c4c4',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        // maxWidth: '438px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputError: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `#f44336 !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #f44336',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        // maxWidth: '438px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputErrorMessage: {
        color: '#f44336',
        fontSize: '0.75rem !important',
        marginTop: '3px',
        lineHeight: '1.66',
        marginLeft: '13px',
    },
    itemGridTelephone: {
        paddingRight: '45px',
        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        },
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        isDirty,
        formData,
        handleChangeData,
        handleChangeCountry,
        countryLov,
        handleClickNextButton,
        errors,
        checkState,
        setCheckState,
        phone,
        pageId,
        totalSteps,
        stateLov,
        handleChangeState,
        companyNameExists,
    } = params;
    const { companyNameError, firstNameError, lastNameError, countryError, emailAddressError, phoneError, contactNameError, stateError } = errors;
    // const isDirty =
    //     formData.companyName === '' && state === '' && country === '' && city === '' && formData.emailAddress === '' && formData.phone === '' && formData.contactName === '';

    const buttonDisable = isDirty && checkState;

    const defns = {
        helloText: { variant: 'h4' },
        textFieldExample: { value: 'Hello TextField' },
        nonReactElementExample: {
            onClick: evt => {
                alert('Clicked!');
            },
        },
        gridBody: {
            container: true,
            spacing: 2,
        },
        gridBodyFooter: {
            container: true,
            spacing: 2,
        },
        itemGrid: { item: true, xl: 6, lg: 6, md: 6, sm: 12, xs: 12 },
        itemGridTelephone: { item: true, xl: 6, lg: 6, md: 6, sm: 12, xs: 12 },
        termsAndConditionsGrid: { item: true, xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        progressGrid: { item: true, md: 2, sm: 6, xs: 6 },
        companyName: {
            name: 'companyName',
            placeholder: '+ Ex. Dream Team',
            onChange: handleChangeData,
            value: formData.companyName,
            error: companyNameError,
            helperText: companyNameError ? 'Company Name is required' : undefined,
            disabled: companyNameExists,
        },
        firstName: {
            name: 'firstName',
            placeholder: '+ Ex. john',
            onChange: handleChangeData,
            value: formData.firstName,
            error: formData.firstName === '' && firstNameError,
            helperText: formData.firstName === '' && firstNameError ? 'First Name is required' : undefined,
            disabled: true,
        },
        lastName: {
            name: 'lastName',
            placeholder: '+ Ex. sparrow',
            onChange: handleChangeData,
            value: formData.lastName,
            error: formData.lastName === '' && lastNameError,
            helperText: formData.lastName === '' && lastNameError ? 'Last Name is required' : undefined,
            disabled: true,
        },
        contactName: {
            name: 'contactName',
            placeholder: '+ Ex. Jack Sparrow',
            onChange: handleChangeData,
            value: formData.contactName,
            error: contactNameError,
            helperText: contactNameError ? 'Point of Contact Name is required' : undefined,
        },
        phone: {
            name: 'phone',
            error: phone === '' && phoneError,
            helperText: formData.phone === '' && phoneError ? 'Telephone Number is required' : undefined,
        },
        emailAddress: {
            name: 'emailAddress',
            placeholder: '+ Ex. john@dreamteam',
            type: 'email',
            onChange: handleChangeData,
            value: formData.emailAddress,
            error: emailAddressError,
            helperText:
                formData.emailAddress === '' && emailAddressError
                    ? 'Email Address is required'
                    : formData.emailAddress !== '' && validateEmail(formData.emailAddress) === null
                    ? 'Please enter a valid email'
                    : undefined,
            disabled: true,
        },
        country: {
            onChange: handleChangeCountry,
            value: formData?.country,
            options: countryLov,
            id: 'country',
            placeholder: '+ Ex. United States',
            error: formData?.country !== '' && countryError,
            helperText: formData?.country !== '' && countryError ? 'Country is required' : undefined,
        },
        state: {
            onChange: handleChangeState,
            value: formData?.state,
            options: stateLov,
            id: 'state',
            placeholder: '+ Ex. Maryland',
            error: formData?.state !== '' && stateError,
            helperText: formData?.state !== '' && stateError ? 'State/Province is required' : undefined,
        },

        footerGridItem: {
            xl: 5,
            lg: 5,
            md: 5,
            sm: 12,
            xs: 12,
        },
        progress: {
            variant: 'determinate',
            value: parseFloat((pageId / totalSteps) * 100).toFixed(2),
        },
        companyNameErrorText: { hidden: !companyNameError },
        lastNameErrorText: { hidden: !lastNameError },
        firstNameErrorText: { hidden: !firstNameError },
        countryErrorText: { hidden: !countryError },
        nextButton: { disabled: !buttonDisable, onClick: handleClickNextButton, color: 'primary' },
        termsAndConditionCheck: {
            checked: checkState,
            onChange: () => setCheckState(!checkState),
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmpStep1Page = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const appContext = useContext(AppContext);
    const { lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const {
        scrollToPageTop,
        empData: formData,
        setEmpData: setFormData,
        pageId,
        setPageID,
        totalSteps,
        surveyUser,
        countryCode,
        setCountryCode,
        stepDirtyList,
        setStepDirtyList,
        companyNameExists,
    } = layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [checkState, setCheckState] = React.useState(false);
    const [phone, setPhone] = useState(formData.phone);
    const [phoneError, setPhoneError] = useState(false);

    const [errors, setErrors] = React.useState({
        companyNameError: false,
        firstNameError: false,
        lastNameError: false,
        emailAddressError: false,
        phoneError: false,
        countryError: false,
        stateError: false,
    });
    // const [formData, setFormData] = React.useState(surveyStep1Data);
    const { countryLov, stateLov, cityLov, country, setCountry, state, setState /*, city, setCity, setCityLocation */ } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov'),
    });

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const step1IsDirty = stepDirtyList?.step1;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    useEffect(() => {
        setFormData({ ...formData, phone });
        if (phone !== '') {
            setErrors({ ...errors, phoneError: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone]);

    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    useEffect(() => {
        if (step1IsDirty) setIsDirty(true);
    }, [step1IsDirty]);

    useEffect(() => {
        setCountry(_get(surveyUser, 'country', null));
        setState(_get(surveyUser, 'state', null));
        // setCity(_get(surveyUser, 'city', null));
        // setCityLocation(_get(surveyUser, 'cityLocation', null));
    }, [surveyUser, setCountry, setState]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeData = React.useCallback(
        evt => {
            const { name, value } = evt.target;
            setErrors({
                companyNameError: false,
                firstNameError: false,
                lastNameError: false,
                emailAddressError: false,
                contactNameError: false,
                phoneError: false,
                countryError: false,
            });
            if (name === 'emailAddress') {
                const result = validateEmail(value);
                if (result === null) setErrors({ ...errors, emailAddressError: true });
            }
            setFormData({ ...formData, [name]: value });
            setStepDirtyList(prev => ({ ...prev, step1: true }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formData],
    );
    const handleChangeCountry = (evt, option) => {
        setFormData({ ...formData, country: option?.name });
        setCountry(option?.name);
        setPhone('');
        setCountryCode(option?.iso2);
        setState(null);
        // setCity(null);
        // setCityLocation(null);
        setStepDirtyList(prev => ({ ...prev, step1: true }));
    };
    const handleChangeState = (evt, option) => {
        setFormData({ ...formData, state: option?.name });
        setState(option?.name);
        // setCity(null);
        // setCityLocation(null);
        setStepDirtyList(prev => ({ ...prev, step1: true }));
    };
    // const handleChangeCity = (evt, option) => {
    //     setCity(option);
    //     setStepDirtyList(prev => ({ ...prev, step1: true }));
    // };

    const validate = () => {
        let newErrors = { ...errors };

        !formData.companyName && _set(newErrors, 'companyNameError', true);
        formData.companyName && _set(newErrors, 'companyNameError', false);

        !formData.firstName && _set(newErrors, 'firstNameError', true);
        formData.firstName && _set(newErrors, 'firstNameError', false);
        !formData.lastName && _set(newErrors, 'lastNameError', true);
        formData.lastName && _set(newErrors, 'lastNameError', false);
        if (phone === '' || phoneError) {
            _set(newErrors, 'phoneError', true);
            setPhoneError(true);
        }
        if (phone && !phoneError) {
            _set(newErrors, 'phoneError', false);
            setPhoneError(false);
        }
        !formData.emailAddress && _set(newErrors, 'emailAddressError', true);
        formData.emailAddress && _set(newErrors, 'emailAddressError', false);
        !formData.country && _set(newErrors, 'countryError', true);
        formData?.country && _set(newErrors, 'countryError', false);
        !formData.state && _set(newErrors, 'stateError', true);
        formData?.state && _set(newErrors, 'countryError', false);

        setErrors(newErrors);
        return !_includes(newErrors, true);
    };

    const handleClickNextButton = () => {
        if (validate() && checkState) setPageID(pageId + 1);
    };

    const handleChangePhone = val => {
        // if (val) {
        //     setPhone(val);
        //     if (isPossiblePhoneNumber(val) && isValidPhoneNumber(val)) {
        //         setPhoneError(false);
        //         setErrors({ ...errors, phoneError: false });
        //     } else {
        //         setPhoneError(true);
        //         setErrors({ ...errors, phoneError: true });
        //     }
        // } else {
        //     setPhoneError(true);
        //     setErrors({ ...errors, phoneError: true });
        // }

        let phoneValid = true;
        if (val) {
            setPhone(val);
            phoneValid = isPossiblePhoneNumber(val) && isValidPhoneNumber(val);
        } else {
            phoneValid = false;
        }

        setPhoneError(!phoneValid);
        setErrors({ ...errors, phoneError: !phoneValid });
        setStepDirtyList(prev => ({ ...prev, step1: true }));
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        isDirty,
        setIsDirty,
        formData,
        setFormData,
        handleChangeData,
        handleChangeCountry,
        handleChangeState,
        // handleChangeCity,
        handleClickNextButton,
        country,
        state,
        // city,
        countryLov,
        stateLov,
        cityLov,
        errors,
        history,
        checkState,
        setCheckState,
        phone,
        pageId,
        totalSteps,
        companyNameExists,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.heading}>Please provide your information below</div>
            <Grid {...defns.gridBody}>
                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>Your Company Name: *</p>
                    <AfitdTextField {...defns.companyName} />
                </Grid>

                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>Your Email: *</p>
                    <AfitdTextField {...defns.emailAddress} />
                </Grid>
                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>First Name: *</p>
                    <AfitdTextField {...defns.firstName} />
                </Grid>
                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>Last Name: *</p>
                    <AfitdTextField {...defns.lastName} />
                </Grid>

                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>Select your Country: *</p>
                    <AfitdCountryAutocomplete {...defns.country} />
                </Grid>
                <Grid {...defns.itemGrid}>
                    <p {...defns.fieldLabel}>Select your State/Province: *</p>
                    <AfitdCountryAutocomplete {...defns.state} />
                </Grid>
                <Grid {...defns.itemGridTelephone}>
                    <p {...defns.fieldLabel}>Your Telephone: *</p>
                    <Input
                        country={countryCode}
                        international
                        withCountryCallingCode
                        className={phoneError ? classes.phoneInputError : classes.phoneInput}
                        value={phone}
                        onChange={val => handleChangePhone(val)}
                        placeholder="Ex. +1 (301) 567-9544"
                    />
                    {phoneError && (
                        <AfitdTypography variant="body2" className={classes.phoneInputErrorMessage}>
                            {phoneError && phone.length > 2 ? 'Phone number is not valid' : 'Telephone is required'}
                        </AfitdTypography>
                    )}
                </Grid>

                <Grid {...defns.termsAndConditionsGrid}>
                    <Checkbox {...defns.termsAndConditionCheck} />
                    <p {...defns.termsAndConditionText}>
                        I and/or the organization that I am submitting this feedback on behalf of completely accept the {` `}
                        <AfitdLink {...defns.termsOfConditionLink} href="/home/FAQ/terms" target="_blank">
                            terms of use
                        </AfitdLink>
                        .
                    </p>
                </Grid>
            </Grid>
            <Grid {...defns.gridBodyFooter}>
                {smMatches ? null : <Grid {...defns.footerGridItem}></Grid>}

                <Grid {...defns.progressGrid}>
                    <p>
                        <b>
                            {pageId} of {totalSteps}
                        </b>
                    </p>
                    <BorderLinearProgress {...defns.progress} />
                </Grid>
                <Grid {...defns.footerGridItem}>
                    <div {...defns.nextButton}>
                        <AfitdButton {...defns.nextButton}>
                            Next {` `}
                            &nbsp;
                            <ArrowRightAltIcon fontSize="small" />
                        </AfitdButton>
                    </div>
                </Grid>
            </Grid>
            {/* </Container> */}
        </div>
    );
};

export default EmpStep1Page;
